import React, { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";
import { Fade } from "react-awesome-reveal";
import axios from "axios";
import { FaPlay } from "react-icons/fa6";

import "swiper/css";
import "swiper/css/pagination";

const VideoCarousel = ({ title, paragraph }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await axios.get(
          "https://mosque-backend-yfae.vercel.app/allVideos",
        );
        setVideos(response.data);
      } catch (err) {
        setError("Unable to sync our video log. Please check your network.");
      } finally {
        setLoading(false);
      }
    };
    fetchVideos();
  }, []);

  // Modern Skeleton UI loading container placeholder
  if (loading) {
    return (
      <div className="w-full max-w-6xl mx-auto px-4 py-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[1, 2, 3, 4].map((n) => (
          <div
            key={n}
            className="bg-white border border-stone-100 rounded-2xl overflow-hidden p-3 animate-pulse"
          >
            <div className="aspect-video bg-stone-200 rounded-xl mb-4" />
            <div className="h-4 bg-stone-200 rounded w-3/4 mx-auto mb-2" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-rose-600 font-medium text-sm">
        {error}
      </div>
    );
  }

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8">
      {/* Header Segments */}
      <div className="text-center mb-12">
        <Fade direction="up" triggerOnce>
          <span className="text-sm font-semibold tracking-widest uppercase text-emerald-800 block mb-2">
            Video Insights
          </span>
          <h2 className="text-4xl font-extrabold text-green-950 tracking-tight mb-3">
            {title}
          </h2>
          {paragraph && (
            <p className="text-gray-500 max-w-xl mx-auto text-sm sm:text-base leading-relaxed">
              {paragraph}
            </p>
          )}
        </Fade>
      </div>

      {/* Styled Swiper Carousel Container */}
      <Swiper
        slidesPerView={1}
        spaceBetween={24}
        breakpoints={{
          640: { slidesPerView: 2 },
          1024: { slidesPerView: 3 },
          1280: { slidesPerView: 4 },
        }}
        pagination={{ clickable: true }}
        autoplay={{ delay: 4000, disableOnInteraction: false }}
        grabCursor={true}
        loop={videos.length > 4} // Only loop dynamically if array exceeds page window bounds
        modules={[Pagination, Autoplay]}
        className="w-full max-w-6xl mx-auto !pb-14 modern-swiper-container"
      >
        {videos.map((video, index) => (
          <SwiperSlide key={index} className="py-2">
            <div className="group relative bg-white border border-stone-100 rounded-2xl overflow-hidden shadow-sm flex flex-col h-full transform transition-all duration-500 ease-out hover:-translate-y-2 hover:shadow-xl">
              {/* Subtle visual border highlight on card target active hover states */}
              <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-emerald-700 to-green-900 rounded-t-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              {/* Video Player Box with Fixed Scaling Aspect Ratios */}
              <div className="w-full aspect-video bg-black relative">
                <iframe
                  className="w-full h-full object-cover relative z-10"
                  src={video.url}
                  title={video.title || `Vlog clip segment ${index + 1}`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>

              {/* Card Meta Content Block */}
              <div className="p-4 bg-white flex-1 flex flex-col justify-between">
                <div className="flex items-start gap-2.5">
                  <span className="mt-1 w-5 h-5 rounded-full bg-emerald-50 text-emerald-800 flex items-center justify-center shrink-0">
                    <FaPlay className="w-2 h-2 ml-0.5" />
                  </span>
                  <h3 className="text-sm sm:text-base font-bold text-gray-800 line-clamp-2 tracking-tight group-hover:text-emerald-950 transition-colors leading-tight">
                    {video.title}
                  </h3>
                </div>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export default VideoCarousel;
